/**
 * Right sidebar shared by CommunityPage and PostPage.
 *
 * Marketplace mode: category filters + helper links (legacy).
 * Community mode (REACT_APP_BETA_MODE): the Task 6 "Skool / Reddit"
 *   layout — About copy, "We're in here daily", Invite People CTA
 *   (opens the existing ShareModal), team avatars, Community
 *   Guidelines link, dynamic member count.
 *
 * Sidebar is a pure component: the parent page (CommunityPage /
 * PostPage) loads `team` + `memberCount` from its own page duck
 * (via loadData → /api/users/query) and passes them down as props.
 */

import React, { useState } from 'react';
import { string, array, object, number, func, bool } from 'prop-types';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { pathByRouteName } from '../../../util/routes';
import {
  ExternalLink,
  NamedLink,
  Avatar,
  PrimaryButton,
  ShareModal,
  Icon,
} from '../../../components';
import routeConfiguration from '../../../routeConfiguration';
import classNames from 'classnames';

import css from './Sidebar.module.css';

/**
 * Generic filter link block — sidebar chip list that drives one
 * `?pub_…=` query parameter. Used for Categories, Disciplines,
 * Roles, and Topics. Multi-enum filters (Roles, Topics) match if
 * the post has *any* of the selected keys.
 */
const FilterLink = ({ option, isSelected, queryParamName, currentSearchParams }) => {
  const searchParams = new URLSearchParams(currentSearchParams);
  searchParams.set(queryParamName, option.key);

  return (
    <li
      className={classNames(css.linkItem, {
        [css.linkItemActive]: isSelected,
      })}
    >
      <NamedLink className={css.link} name="CommunityPage" to={{ search: searchParams.toString() }}>
        {option.label}
      </NamedLink>
    </li>
  );
};

const AllOptionsLink = ({ queryParamName, currentSearchParams }) => {
  const filteredParams = Object.fromEntries(
    Object.entries(currentSearchParams || {}).filter(([key]) => key !== queryParamName)
  );
  const searchParamsString = new URLSearchParams(filteredParams).toString();

  return (
    <li className={css.linkItem}>
      <NamedLink className={css.link} name="CommunityPage" to={{ search: searchParamsString }}>
        <FormattedMessage id="CommunityPage.allPosts" />
      </NamedLink>
    </li>
  );
};

const FilterLinks = ({ className, options, queryParamName, currentSearchParams }) => {
  const classes = classNames(css.links, className);
  // For multi-enum filter values the URL carries a comma-joined list,
  // so a chip is "selected" when its key is present.
  const raw = currentSearchParams?.[queryParamName] || '';
  const selectedSet = new Set(raw.split(',').filter(Boolean));

  return (
    <ul className={classes}>
      {options.map(option => (
        <FilterLink
          key={option.key}
          option={option}
          queryParamName={queryParamName}
          isSelected={selectedSet.has(option.key)}
          currentSearchParams={currentSearchParams}
        />
      ))}

      <AllOptionsLink queryParamName={queryParamName} currentSearchParams={currentSearchParams} />
    </ul>
  );
};

FilterLinks.propTypes = {
  className: string,
  options: array.isRequired,
  queryParamName: string.isRequired,
  currentSearchParams: object,
};

/**
 * Collapsible filter section — wraps a FilterLinks group with a
 * clickable header + chevron. Opens by default when:
 *   - `defaultOpen` is true (used to keep Categories open at start), or
 *   - the section's `queryParamName` has any selected value in the
 *     current URL (so navigating to a filter expands its group)
 *
 * Lazy useState initializer derives the initial open state once; the
 * user's subsequent toggles take over.
 */
const CollapsibleFilterSection = ({
  titleId,
  options,
  queryParamName,
  currentSearchParams,
  defaultOpen,
}) => {
  const hasSelection = (() => {
    const raw = currentSearchParams?.[queryParamName] || '';
    if (!raw) return false;
    const selected = new Set(raw.split(',').filter(Boolean));
    return options.some(o => selected.has(o.key));
  })();
  const [isOpen, setIsOpen] = useState(() => defaultOpen || hasSelection);

  if (!options || options.length === 0) return null;

  return (
    <div className={css.communitySection}>
      <button
        type="button"
        className={classNames(css.sectionToggle, {
          [css.sectionToggleOpen]: isOpen,
        })}
        onClick={() => setIsOpen(o => !o)}
        aria-expanded={isOpen}
      >
        <h3 className={css.sectionTitle}>
          <FormattedMessage id={titleId} />
        </h3>
        <Icon
          name={isOpen ? 'chevronUp' : 'chevronDown'}
          size={14}
          rootClassName={css.sectionChevron}
        />
      </button>
      {isOpen ? (
        <FilterLinks
          className={css.communityCategoryLinks}
          options={options}
          queryParamName={queryParamName}
          currentSearchParams={currentSearchParams}
        />
      ) : null}
    </div>
  );
};

CollapsibleFilterSection.defaultProps = {
  options: [],
  currentSearchParams: null,
  defaultOpen: false,
};
CollapsibleFilterSection.propTypes = {
  titleId: string.isRequired,
  options: array,
  queryParamName: string.isRequired,
  currentSearchParams: object,
  defaultOpen: bool,
};

const HelperLinks = ({ className }) => {
  const classes = classNames(css.links, className);
  const routes = routeConfiguration();
  const helpPagePath = pathByRouteName('HelpPage', routes);

  return (
    <ul className={classes}>
      <li className={css.linkItem}>
        <ExternalLink className={css.link} href={helpPagePath}>
          <FormattedMessage id="CommunityPage.helpLink" />
        </ExternalLink>
      </li>
    </ul>
  );
};

HelperLinks.propTypes = {
  className: string,
};

/**
 * Community mode sidebar (Task 6). Self-contained — owns its own
 * ShareModal so any parent page renders <Sidebar /> without extra
 * wiring. Dispatches fetchCommunitySidebar via a lazy useState
 * initializer (one-shot guard inside the thunk handles re-mounts).
 */
const CommunitySidebar = ({
  rootClassName,
  className,
  categories,
  disciplines,
  roles,
  topics,
  currentSearchParams,
  team = [],
  memberCount = 0,
  onManageDisableScrolling,
}) => {
  const intl = useIntl();
  const [isShareOpen, setShareOpen] = useState(false);
  const classes = classNames(rootClassName || css.root, css.communityRoot, className);

  // ShareModal builds its share URLs from `location.pathname`; point
  // at /community so an invited user lands on the forum.
  const shareLocation = { pathname: '/community' };

  return (
    <div className={classes}>
      <div className={css.communitySection}>
        <h3 className={css.aboutHeadline}>
          <FormattedMessage id="CommunitySidebar.aboutHeadline" />
        </h3>
        <p className={css.aboutSubtext}>
          <FormattedMessage id="CommunitySidebar.aboutSubtext" />
        </p>
        <p className={css.aboutAttribution}>
          <FormattedMessage id="CommunitySidebar.attribution" />
        </p>
        <PrimaryButton
          className={css.inviteButton}
          onClick={() => setShareOpen(true)}
          type="button"
        >
          <FormattedMessage id="CommunitySidebar.invitePeople" />
        </PrimaryButton>
      </div>

      <div className={css.communitySection}>
        <h3 className={css.sectionTitle}>
          <FormattedMessage id="CommunitySidebar.teamTitle" />
        </h3>
        <ul className={css.teamList}>
          {team.map(member => {
            const id = member?.id?.uuid;
            const name =
              member?.attributes?.profile?.displayName ||
              member?.attributes?.profile?.abbreviatedName ||
              '';
            return (
              <li key={id || name} className={css.teamItem}>
                {id ? (
                  <NamedLink name="ProfilePage" params={{ id }} className={css.teamLink}>
                    <Avatar className={css.teamAvatar} user={member} disableProfileLink />
                    <span className={css.teamName}>{name}</span>
                  </NamedLink>
                ) : (
                  <span className={css.teamLink}>
                    <Avatar className={css.teamAvatar} user={member} disableProfileLink />
                    <span className={css.teamName}>{name}</span>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {memberCount > 0 ? (
        <div className={css.communitySection}>
          <p className={css.memberCount}>
            <FormattedMessage id="CommunitySidebar.memberCount" values={{ count: memberCount }} />
          </p>
        </div>
      ) : null}

      <CollapsibleFilterSection
        titleId="CommunityPage.categories"
        options={categories}
        queryParamName="pub_category"
        currentSearchParams={currentSearchParams}
        defaultOpen
      />
      <CollapsibleFilterSection
        titleId="CommunitySidebar.topicsTitle"
        options={topics}
        queryParamName="pub_topics"
        currentSearchParams={currentSearchParams}
      />
      <CollapsibleFilterSection
        titleId="CommunitySidebar.disciplinesTitle"
        options={disciplines}
        queryParamName="pub_discipline"
        currentSearchParams={currentSearchParams}
      />
      <CollapsibleFilterSection
        titleId="CommunitySidebar.rolesTitle"
        options={roles}
        queryParamName="pub_role"
        currentSearchParams={currentSearchParams}
      />

      <ShareModal
        id="CommunitySidebar.shareModal"
        location={shareLocation}
        isOpen={isShareOpen}
        onCloseModal={() => setShareOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        titleMessage={intl.formatMessage({ id: 'CommunitySidebar.shareModalTitle' })}
      />
    </div>
  );
};

CommunitySidebar.defaultProps = {
  rootClassName: null,
  className: null,
  categories: [],
  disciplines: [],
  roles: [],
  topics: [],
  currentSearchParams: null,
  team: [],
  memberCount: 0,
  onManageDisableScrolling: () => null,
};
CommunitySidebar.propTypes = {
  rootClassName: string,
  className: string,
  categories: array,
  disciplines: array,
  roles: array,
  topics: array,
  currentSearchParams: object,
  team: array,
  memberCount: number,
  onManageDisableScrolling: func,
};

/**
 * Marketplace-mode sidebar (categories + helper links). Kept intact
 * so the non-beta marketplace forum behaves exactly as before.
 */
const MarketplaceSidebar = ({ rootClassName, className, categories, currentSearchParams }) => {
  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      <div className={css.linksWrapper}>
        <h3 className={css.title}>
          <FormattedMessage id="CommunityPage.categories" />
        </h3>
        <FilterLinks
          options={categories}
          queryParamName="pub_category"
          currentSearchParams={currentSearchParams}
        />
      </div>

      <div className={classNames(css.linksWrapper, css.helperLinksWrapper)}>
        <h3 className={css.title}>
          <FormattedMessage id="CommunityPage.links" />
        </h3>
        <HelperLinks />
      </div>
    </div>
  );
};

MarketplaceSidebar.defaultProps = { rootClassName: null, className: null };
MarketplaceSidebar.propTypes = {
  rootClassName: string,
  className: string,
  categories: array.isRequired,
  currentSearchParams: object,
};

/**
 * Community sidebar (team members, invite, about) is now shown in every mode
 * so the beta and full-marketplace deployments render the same /community
 * page. Previously this switched to MarketplaceSidebar (category + helper
 * links) when REACT_APP_BETA_MODE was off. The sidebar team data is fetched
 * unconditionally in loadData, so it's populated regardless of the flag.
 */
const Sidebar = props => {
  return <CommunitySidebar {...props} />;
};

Sidebar.defaultProps = {
  rootClassName: null,
  className: null,
};

Sidebar.propTypes = {
  rootClassName: string,
  className: string,
  categories: array.isRequired,
  disciplines: array,
  roles: array,
  topics: array,
  currentSearchParams: object,
  team: array,
  memberCount: number,
  onManageDisableScrolling: func,
};

export default Sidebar;
